import { generatePath, matchPath } from "react-router-dom"
import { Location } from "history"
import merge from "lodash/merge"
import difference from "lodash/difference"
import { Roles } from "../../mp-utils/generic-utils"
import isEqual from "lodash/isEqual"
import { format } from "date-fns"
import {
  DETAILS_DATE_FORMAT,
  FILTER_ITEMS,
  FILTERS_REQUEST_PARAMS,
  MODALS_REQUEST_PARAMS_BY_ENTITY,
  ROLE_CHIP_COLOR,
  ROLES_BY_ROUTE,
  UNTIMED_ENTITIES,
  EXPORT_SUBPAGE_PATH_MAPPING
} from "../constants"
import { CategoriesState, FiltersState } from "../states"
import { durationFormatter, formatDate } from "../../mp-core/helpers/formatters"
import { IndentationDropdownOptions } from "../../mp-shared-components/generic-shared-components/components/IndentationDropdown"
import {
  CubeRequestParams,
  CubeRequestTimeDimension,
  DetailsDataFormat,
  DetailsDataParams,
  DetailsSubtitleData,
  NavItem
} from "../types"
import {
  getDateFilterByMaxLimit,
  getScoreRangeFilters,
  getTestFilterRequest
} from "./filters"
import { formatDetailsDateValue } from "./detailsFormatters"
import { GRADUATED_LABEL, NO_GRADE_LABEL } from "../constants/filters"
import { FeatureFlagStatesType } from "../../mp-utils/generic-utils/feature-flags/types"
import { Role } from "../../mp-database/types/Role"
import { formatLocalDateToUTCDate } from "../../mp-core/helpers/formatters/date"
import { ProgramCourseData } from "../../snapcourse/authoring/pages/SnapCourseConfiguration/types"
import { ProgramAndCoursesInfo } from "../../../types/gqlTypes/graphql"
import {
  GOALS_SCORE_RANGE_BY_SUBJECT_SUBPAGE,
  GOALS_SCORE_RANGE_BY_TEST_SUBPAGE
} from "../constants/tables/performance"
import { getGoalsNotDiagnosticFilter } from "./scores"

export * from "./api"
export * from "./common"
export * from "./details"
export * from "./filters"
export * from "./goals"
export * from "./nav"
export * from "./cubeColumnMapping"
export * from "./rowActions"
export * from "./tables"
export * from "./widgets"
export * from "./scores"
export * from "./questions"
export * from "./dateRange"
export * from "./generateCommonColumns"
export * from "./specificExportCallbacks"

export const transformFilterOptions = (
  filter: string,
  items: any[]
): IndentationDropdownOptions[] => {
  if (!items) {
    return []
  }

  return items.map(item => {
    const [idKey, labelKey] = FILTERS_REQUEST_PARAMS[filter].dimensions
    return {
      value: item[idKey],
      label: item[labelKey ?? idKey],
      optionDetails: item
    }
  })
}

/**
 * Get all id-based filters from filter bar.
 */
export const getCommonFilters = (
  filters: FiltersState,
  filterMemberMap?: Record<string, string>,
  options?: Partial<{
    include: string[]
    exclude: string[]
  }>
): CubeRequestParams["filters"] => {
  if (!filters) {
    return []
  }

  let allowedFilters = Object.entries(filters)

  if (options?.exclude) {
    // Remove excluded filters, if present.
    allowedFilters = allowedFilters.filter(
      ([filter]) => !options.exclude.includes(filter)
    )
  }

  if (options?.include) {
    // Keep only included filters, if present.
    allowedFilters = allowedFilters.filter(([filter]) =>
      options.include.includes(filter)
    )
  }

  // Filter out "date" and "boolean" filters. They will be added separately.
  const requestFilters = allowedFilters
    .filter(([filter, values]) => {
      const filterItem = FILTER_ITEMS.find(
        filterItem => filterItem.key === filter
      )

      return (
        !["date", "boolean"].includes(filterItem?.type) && values?.length > 0
      )
    })
    .reduce((res, [filter, values]) => {
      const filterMembers = FILTERS_REQUEST_PARAMS[filter]?.dimensions
      const member =
        filterMemberMap?.[filterMembers?.[0]] ?? filterMemberMap?.[filter]

      if (filter === "test") {
        const testFilterRequest = getTestFilterRequest(values, filterMemberMap)

        if (!testFilterRequest) {
          return res
        }

        return [...res, testFilterRequest]
      }

      if (filter === "score") {
        const { testFilter, scoreFilters } = getScoreRangeFilters(
          values,
          filterMemberMap
        )

        if (!testFilter) {
          return res
        }

        return [...res, ...scoreFilters]
      }

      if (["skill", "standard"].includes(filter)) {
        if (!member) {
          return res
        }

        return [
          ...res,
          {
            member,
            operator: "contains",
            values
          }
        ]
      }

      if (
        filter === "grade" &&
        ![
          "org_view_students",
          "org_view_classes",
          "avegare_time_spent_view",
          "scoring_by_subject_view",
          "scoring_by_program_view",
          "goals_summary_by_program",
          "goals_summary_by_subject",
          "usage_students",
          "scores_view",
          "scores_subtests_view"
        ].includes(member?.split(".")?.[0])
      ) {
        return res
      }

      if (!member) {
        return res
      }

      if (filter === "content" && member === "scores_view.subtestIds") {
        return [
          ...res,
          {
            member,
            operator: "contains",
            values
          }
        ]
      }

      return [
        ...res,
        {
          member,
          operator: "equals",
          values
        }
      ]
    }, [])

  return requestFilters
}

export const getTimeDimensions = (
  dateRange: string[],
  entity: string,
  dimension?: string
) => {
  if (!!dateRange?.[0] && !!dateRange?.[1]) {
    if (UNTIMED_ENTITIES.includes(entity)) {
      return {}
    }

    // TODO: add after BE fix
    // if (PART_TIMED_ENTITIES.includes(entity)) {
    //   return {
    //     timeDimensions: [
    //       {
    //         dimension: dimension ?? `${entity}.createdAt`,
    //         dateRange: [FIRST_DATE, dateRange[1]]
    //       }
    //     ]
    //   }
    // }

    const formattedStartDate = formatLocalDateToUTCDate({
      date: dateRange[0],
      withTime: true,
      withSeconds: true,
      useStartOfDay: true,
      useISOFormat: true
    })

    const formattedEndDate = formatLocalDateToUTCDate({
      date: dateRange[1],
      withTime: true,
      withSeconds: true,
      useEndOfDay: true,
      useISOFormat: true
    })

    return {
      timeDimensions: [
        {
          dimension: dimension ?? `${entity}.createdAt`,
          dateRange: [formattedStartDate, formattedEndDate]
        }
      ]
    }
  }

  return {}
}

export const getWidgetTimeDimensions = (
  dateRange: string[],
  widgetTimeDimensions: CubeRequestTimeDimension[]
) => {
  if (dateRange?.length > 0) {
    return {
      timeDimensions: widgetTimeDimensions.map(timeDimension => ({
        ...timeDimension,
        dateRange
      }))
    }
  }

  return {}
}

export const getBooleanFilters = (
  filters: FiltersState,
  filterMemberMap?: Record<string, string>
): CubeRequestParams["filters"] => {
  if (!filters) {
    return []
  }

  const booleanFilters = Object.entries(filters)
    .filter(
      ([filter]) =>
        FILTER_ITEMS.find(filterItem => filterItem.key === filter)?.type ===
        "boolean"
    )
    .map(([filter, values]) => {
      const filterMember = FILTERS_REQUEST_PARAMS[filter].filters[0].member

      return {
        ...FILTERS_REQUEST_PARAMS[filter].filters[0],
        member: filterMemberMap?.[filterMember] ?? filterMember,
        values
      }
    })

  return booleanFilters
}

export const getEntityFilters = (
  entityIdProp: string,
  entityId: string | string[]
): CubeRequestParams["filters"] => {
  if (!entityIdProp || !entityId || !entityId.length) {
    return []
  }

  return [
    {
      member: entityIdProp,
      operator: "equals",
      values: Array.isArray(entityId) ? entityId : [entityId]
    }
  ]
}

export const getAdditionalFiltersByRoleAndPage = (args: {
  page: string
  subpage: string
  userRole: Role
}) => {
  const { page, subpage, userRole } = args

  if (page === "organization" && subpage === "admins") {
    if (userRole === "District Admin") {
      return [
        {
          member: "org_view_admins.role",
          operator: "equals",
          values: ["District Admin", "School Admin"]
        }
      ]
    }

    if (userRole === "School Admin") {
      return [
        {
          member: "org_view_admins.role",
          operator: "equals",
          values: ["School Admin"]
        }
      ]
    }
  }

  if (page === "insights") {
    if (subpage === GOALS_SCORE_RANGE_BY_TEST_SUBPAGE) {
      return getGoalsNotDiagnosticFilter("test_view_students_by_test")
    }

    if (subpage === GOALS_SCORE_RANGE_BY_SUBJECT_SUBPAGE) {
      return getGoalsNotDiagnosticFilter("test_view_students_by_subject")
    }

    if (subpage === "activity-log" && userRole === "Teacher") {
      return [
        {
          member: "usage_activity_logs.userRole",
          operator: "equals",
          values: ["Student"]
        }
      ]
    }
  }

  return []
}

export const getDateFilterWithMaxLimitByPageAndSubpage = (args: {
  page: string
  subpage: string
  date: string[]
}) => {
  const { page, subpage, date } = args

  if (!page || !subpage || !date) return []

  if (page === "organization") {
    if (subpage === "states") {
      return getDateFilterByMaxLimit(date, "org_view_states.createdAt")
    }
    if (subpage === "districts") {
      return getDateFilterByMaxLimit(date, "org_view_districts.createdAt")
    }
    if (subpage === "admins") {
      return getDateFilterByMaxLimit(date, "org_view_admins.createdAt")
    }
    if (subpage === "schools") {
      return getDateFilterByMaxLimit(date, "org_view_schools.createdAt")
    }
    if (subpage === "teachers") {
      return getDateFilterByMaxLimit(date, "org_view_teachers.createdAt")
    }
    if (subpage === "classes") {
      return getDateFilterByMaxLimit(date, "org_view_classes.createdAt")
    }
    if (subpage === "students") {
      return getDateFilterByMaxLimit(date, "org_view_students.createdAt")
    }
  }

  return []
}

export const getSearchFilters = (
  filters: FiltersState,
  searchProps: string[],
  searchString: string
): CubeRequestParams["filters"] => {
  if (!filters || !searchProps?.length || !searchString) {
    return []
  }

  if (searchProps.length === 1) {
    return [
      {
        member: searchProps[0],
        operator: "contains",
        values: [searchString]
      }
    ]
  }

  return [
    {
      or: searchProps.map(item => ({
        member: item,
        operator: "contains",
        values: [searchString]
      }))
    }
  ]
}

export const getRouteRolesByPathname = (pathname: string) =>
  Object.entries(ROLES_BY_ROUTE).find(([route]) =>
    matchPath(pathname, { path: route, exact: true })
  )?.[1] ?? []

export const getRouteRoles = (location: Location) =>
  getRouteRolesByPathname(location.pathname)

export const getUserScopeFilters = (user: any): FiltersState => {
  switch (user.role) {
    case Roles.Super_Administrator: {
      return {}
    }
    // case Roles.ACT_Admin: {
    //   return {
    //     test: ["EACT"]
    //   }
    // }
    case Roles.District_Admin: {
      return {
        state: [user.state],
        district: [user.schoolDistrict]
      }
    }
    // case Roles.ACT_Multi_District_Admin: {
    //   // Use districts array if available, otherwise fall back to schoolDistrict
    //   const districts =
    //     user.districts && user.districts.length > 0
    //       ? user.districts
    //       : user.schoolDistrict
    //       ? [user.schoolDistrict]
    //       : []

    //   return {
    //     district: districts,
    //     test: ["EACT"]
    //   }
    // }
    case Roles.School_Admin: {
      return {
        state: [user.state],
        district: [user.schoolDistrict],
        school: [user.school]
      }
    }
    case Roles.Teacher: {
      // For teachers with multiple schools, don't pre-set school filter
      // This allows them to select which schools they want
      const hasMultipleSchools = user.schools && user.schools.length > 1

      const filters: FiltersState = {
        state: [user.state],
        district: [user.schoolDistrict],
        teacher: [user.id]
      }

      // Only set school filter if teacher has single school
      if (!hasMultipleSchools) {
        filters.school = [user.school]
      }

      return filters
    }
    case Roles.Student: {
      return {
        state: [user.state],
        district: [user.schoolDistrict],
        school: [user.school],
        student: [user.id]
      }
    }
    default: {
      return {}
    }
  }
}

const handleDifference = (
  key: string,
  value: string[],
  fixedFilterValue: string[]
) => {
  if (key === "date") {
    return isEqual(value, fixedFilterValue ?? []) ? [] : value
  }

  return difference(value, fixedFilterValue ?? [])
}

export const removeFixedFilters = (
  filters: FiltersState,
  fixedFilters?: FiltersState
): FiltersState => {
  if (!fixedFilters) {
    return filters
  }

  return Object.fromEntries(
    Object.entries(filters).map(([key, value]) => {
      return [key, handleDifference(key, value, fixedFilters[key])]
    })
  )
}

// Convert query data to filters state.
export const transformQueryFilters = (
  queryFilters: Record<string, string>,
  fixedFilters?: FiltersState
): FiltersState => {
  const state = Object.fromEntries(
    Object.entries(queryFilters).map(([filter, value]) => [
      filter,
      Array.isArray(value) ? value : [value]
    ])
  )

  if (!fixedFilters) {
    return state
  }

  return merge({}, fixedFilters ?? {}, state)
}

export const getTitleParts = (
  items: NavItem[],
  path: string,
  params: Record<string, string>,
  level = 0,
  defaultParts = []
) => {
  const pathParts = path.split("/").slice(1)
  const levelParam = pathParts[level]
    // Remove route-specific symbols (":" and "?")
    ?.replaceAll(/:|\?/g, "")
    // Remove fixed params
    ?.replace(/\([^)]*\)/g, "")

  if (!params[levelParam]) {
    return defaultParts
  }

  const item = items.find(
    item =>
      item.href ===
      generatePath(`/${pathParts.slice(0, level + 1).join("/")}`, params)
  )

  if (item?.children) {
    return getTitleParts(item.children, path, params, level + 1, [
      item.label,
      ...defaultParts
    ])
  }

  if (item) {
    return [item.label, ...defaultParts]
  }

  return defaultParts
}

/**
 * Get file name from content-disposition string
 * (ex. "attachment; filename="example.txt")
 */
export const getFileName = (contentDisposition?: string): string => {
  let filename = "data.csv"

  if (contentDisposition) {
    const filenameMatch = contentDisposition?.match(
      /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
    )

    if (filenameMatch !== null && filenameMatch[1]) {
      filename = filenameMatch[1].replace(/['"]/g, "")
    }
  }

  return filename
}

export const getDetailsFormattedValue = (
  value: string | Date,
  formatType?: DetailsDataFormat,
  details?: {
    categories?: CategoriesState
  }
) => {
  if (!value) return value || "--"

  const { categories } = details || {}

  switch (formatType) {
    case "date": {
      return formatDetailsDateValue(value)
    }
    case "dateTime": {
      return formatDate(value, true, true, false, false, true)
    }
    case "duration": {
      return durationFormatter.format(Number(value), true)
    }
    case "grade": {
      if (Array.isArray(value)) {
        return !value?.length ? NO_GRADE_LABEL : value.join(", ")
      }

      if (value === "graduated") {
        return GRADUATED_LABEL
      }

      return !value || value === "unknown" ? NO_GRADE_LABEL : value
    }
    case "reportingCategory": {
      return Array.isArray(value)
        ? value.map(item => categories.get(item)).join(";")
        : categories.get(value as string)
    }
    default: {
      return value || "--"
    }
  }
}

export const getBooleanValue = (value: string | boolean): boolean => {
  if (typeof value === "boolean") return value

  if (value?.toLowerCase() === "true") return true

  return false
}

export const getEntityPath = (
  data: any,
  path: DetailsDataParams
): DetailsDataParams => {
  const entity = path.entity

  switch (entity) {
    case "Admin": {
      const role = data?.["org_view_admins.role"]

      let subtitlesData: DetailsSubtitleData[] = []

      if (role === Roles.District_Admin) {
        subtitlesData.push({
          subtitle: "org_view_admins.districtName",
          subtitleEntity: "districts",
          subtitleLink: "org_view_admins.districtId"
        })
      }

      if (role === Roles.School_Admin) {
        subtitlesData.push({
          subtitle: "org_view_admins.schoolName",
          subtitleEntity: "schools",
          subtitleLink: "org_view_admins.schoolId"
        })
      }

      if (role === Roles.ACT_Admin) {
        subtitlesData.push({
          subtitle: "Organization-wide"
        })
      }

      if (role === Roles.ACT_Multi_District_Admin) {
        subtitlesData.push({
          subtitle: "org_view_admins.districtName",
          subtitleEntity: "districts",
          subtitleLink: "org_view_admins.districtId"
        })
      }

      return {
        ...path,
        subtitlesData,
        chips: [
          {
            value: "org_view_admins.role",
            color: ROLE_CHIP_COLOR[role]
          }
        ]
      }
    }
    default: {
      return path
    }
  }
}

export const transformAutocompleteOptions = (
  entity: string,
  items: any[]
): IndentationDropdownOptions[] => {
  if (!items) {
    return []
  }

  return items.map(item => {
    const [idKey, labelKey] = MODALS_REQUEST_PARAMS_BY_ENTITY[entity].dimensions
    return {
      value: item[idKey],
      label: item[labelKey ?? idKey],
      optionDetails: item
    }
  })
}

export const getContentSubpage = (subpage: string, filters: FiltersState) => {
  const contentTypes = filters?.contentType

  if (contentTypes?.length === 1) {
    return `${subpage}/${contentTypes[0].toLowerCase().replaceAll("_", "-")}`
  }

  return subpage
}

export const getEntitySubpageView = (
  page: string,
  subpage: string,
  entityType?: string
) => {
  if (page === "content") {
    return `${subpage}/${entityType}_id`
  }
  return `${subpage}_id`
}

export const getExportPath = (page: string, subpage: string): string => {
  return `/${EXPORT_SUBPAGE_PATH_MAPPING[page]?.[subpage] || subpage}`
}

export const getExportFeatureFlagParams = (
  page: string,
  subpage: string,
  featureFlags: FeatureFlagStatesType
) => {
  return {
    isReportingAverageTimeSpentData:
      !!featureFlags.reporting_insights_activity_log_time_spent,
    isReportingAverageDaysActiveData:
      !!featureFlags.reporting_average_days_active_data,
    isReportingGradeEnabled: !!featureFlags.reporting_grade_column,

    ...(page === "insights" &&
    ["questions", "studentQuestions"].includes(subpage)
      ? {
          isReportingQuestionsStandardEnabled:
            !!featureFlags.reporting_insights_performance_questions_standard,
          isReportingQuestionsViewedCountEnabled:
            !!featureFlags.reporting_insights_performance_questions_viewed
        }
      : {}),

    ...(page === "content"
      ? {
          isReportingContentStandardsEnabled:
            !!featureFlags.reporting_content_library_standards,
          isReportingAverageAccuracyEnabled:
            !!featureFlags.reporting_content_library_average_accuracy,
          isReportingContentReportingCategoriesEnabled:
            !!featureFlags.reporting_content_library_reporting_categories
        }
      : {})
  }
}

export const checkMandatoryFilterList = (
  filters: FiltersState,
  filterKeyList: string[]
) => {
  if (!filters || !filterKeyList) return false

  return filterKeyList.every(
    filterKey =>
      Array.isArray(filters[filterKey] ?? []) &&
      filters[filterKey]?.length === 1
  )
}

export const getProgramAndCoursesOptions = (
  programsAndCourses: ProgramAndCoursesInfo[]
): ProgramCourseData[] => {
  return programsAndCourses.map(i => ({
    program: i.program,
    programDisplayName: i.programInfo.displayName,
    courses: i.courses.map(course => course.name)
  }))
}

export const getNavItemKey = (item: NavItem) =>
  item.key ?? item.href ?? item.label
